/**
 *  @class
 *  @function Pagination
 */

if (!customElements.get('pagination-theme')) {
  class Pagination extends HTMLElement {
    constructor() {
      super();
      const id = this.dataset.section;
      // Infinite scroll's Load more is never shown (pagination.scss): it
      // has no button.
      this.button = this.classList.contains('pagination-type--loadmore') ? this.querySelector('.load-more') : null;
      this.error = this.querySelector('.pagination__error');
      this.grid = document.querySelector('[data-id=' + id + ']');
      this.animations_enabled = document.body.classList.contains('animations-true') && typeof gsap !== 'undefined';
      // The next page to ask for, and the last there is.
      this.i = (parseInt(this.dataset.page, 10) || 1) + 1;
      this.pages = parseInt(this.dataset.pages, 10) || Infinity;
      this.page_size = this.dataset.pageSize;
      // One page request at a time per grid (themeFetch).
      this.fetchKey = `pagination-${id}`;
    }
    connectedCallback() {
      if (this.i > this.pages) {
        this.finish();
        return;
      }
      if (this.classList.contains('pagination-type--loadmore')) {
        this.loadMore();
      }
      if (this.classList.contains('pagination-type--infinite')) {
        this.infinite();
      }
      this.querySelector('.pagination__retry')?.addEventListener('click', () => this.retry());
    }
    // Filters re-render the grid, and this with it: a page still on its way
    // has nowhere to go.
    disconnectedCallback() {
      this.observer?.disconnect();
      clearTimeout(this.retryTimer);
      clearTimeout(this.announceTimer);
      themeFetch.abort(this.fetchKey);
    }
    // The pages follow the filters and sort the grid was rendered with
    // (data-url, its next page), which the address may no longer show, eg.
    // after a Back that couldn't load.
    pageUrl() {
      const url = new URL(this.dataset.url || window.location.href, window.location.href);
      url.searchParams.set('page', this.i);
      return url.pathname + url.search;
    }
    loadMore() {
      let base = this;
      // Focus stays on the button (a mouse click draws no ring under
      // :focus-visible), so showError and renderProducts can hand it on.
      this.button.addEventListener('click', function () {
        base.loadProducts();
      });
    }
    infinite() {
      let base = this;
      base.observer = new IntersectionObserver(function (entries) {
        if (entries[0].intersectionRatio === 1) {
          base.loadProducts();
        }
      }, {
        threshold: [0, 1]
      });
      base.observer.observe(base);
    }
    loadProducts() {
      if (this.done || this.getAttribute('loading')) {
        return;
      }
      const url = this.pageUrl(),
        page = this.i;
      this.i++;
      this.setAttribute('loading', true);
      themeFetch(url, {}, this.fetchKey)
        .then((response) => response.text())
        .then((html) => {
          const grid = new DOMParser().parseFromString(html, 'text/html').getElementById('product-grid');
          // A page there should be that came back without the grid (eg. an
          // error page sent as 200) failed like any other.
          if (!grid && page <= this.pages) {
            throw new Error(`No product grid in ${url}`);
          }
          return grid;
        })
        .then((grid) => {
          this.failures = 0;
          this.retried = false;
          this.renderProducts(grid, url);
        }, (error) => {
          if (isAbortError(error)) return;
          // Ask for the same page again next time.
          console.error(error);
          this.i--;
          this.removeAttribute('loading');
          this.showError(true, this.retried);
          this.retried = false;
          this.retryInfinite();
        })
        .catch((error) => {
          // Adding the page failed: never leave it loading.
          console.error(error);
          this.removeAttribute('loading');
        });
    }
    // Infinite scroll only asks again when the end of the grid comes into
    // view, so after a failure, while it stays in view, it watches it anew
    // after a pause that doubles with each failure in a row (up to 30s).
    // Those tries leave the message up until one succeeds.
    retryInfinite() {
      if (!this.observer || !this.isConnected) return;
      this.failures = (this.failures || 0) + 1;
      this.observer.unobserve(this);
      clearTimeout(this.retryTimer);
      this.retryTimer = setTimeout(() => {
        if (!this.done) this.observer.observe(this);
      }, Math.min(2000 * 2 ** (this.failures - 1), 30000));
    }
    // The message's button: asks for the page that failed, now. Load more
    // takes the message's place (and its focus, see showError). Infinite
    // scroll has nothing to put there: the message stays, focus on its
    // button, until the page is in (renderProducts) or fails again, which
    // is then said again.
    retry() {
      clearTimeout(this.retryTimer);
      if (this.button) {
        this.showError(false);
      } else {
        this.retried = true;
      }
      this.loadProducts();
      if (this.observer && !this.done) this.observer.observe(this);
    }
    // Says a page couldn't be loaded, in place of Load more, and moves the
    // focus between the two as they swap. Its text is set as it shows so
    // that it is announced; `again` says it once more while it shows.
    showError(show = true, again = false) {
      if (!this.error) return;
      const text = this.error.querySelector('.pagination__error-text'),
        message = window.theme?.strings?.loadMoreError || text?.textContent;
      // Already in that state.
      if (this.error.hidden !== show) {
        if (show && again && text) {
          text.textContent = '';
          clearTimeout(this.announceTimer);
          this.announceTimer = setTimeout(() => {
            text.textContent = message;
          }, 100);
        }
        return;
      }
      const focused = show ? this.button?.contains(document.activeElement) : this.error.contains(document.activeElement);
      if (show && text) text.textContent = message;
      this.error.hidden = !show;
      if (this.button) this.button.hidden = show;
      if (focused) (show ? this.error.querySelector('.pagination__retry') : this.button)?.focus();
    }
    // The page is in. The end of the grid may have stayed in view all the
    // while: the observer only reports a change, so it is asked to look
    // again.
    loaded() {
      this.removeAttribute('loading');
      if (this.observer && !this.done && this.isConnected) {
        this.observer.unobserve(this);
        this.observer.observe(this);
      }
    }
    // pagination:page-changed goes out once the page's products are in the
    // grid (a view transition adds them a frame later), so a listener finds
    // them there (grid-density.js sets their image sizes).
    renderProducts(grid, url) {
      let base = this,
        // Past the last page the grid is the empty state, or not there.
        products = grid ? Array.from(grid.querySelectorAll('.column')) : [],
        last = !products.length || products.length < this.page_size || this.i > this.pages,
        // Focus on what goes now (infinite scroll's Try again, with the
        // message, or Load more after the last page) goes on to the first
        // new product, or the last one there is.
        focused = this.button ? last && this.button.contains(document.activeElement) : this.error?.contains(document.activeElement);

      const settle = () => {
        if (focused) (products[0] || this.grid?.lastElementChild)?.querySelector('a[href]')?.focus();
        this.showError(false);
        dispatchCustomEvent('pagination:page-changed', {
          url: url
        });
      };

      if (!products.length) {
        settle();
        base.removeAttribute('loading');
        base.finish();
        return;
      }

      if (last) {
        this.finish();
      }

      const appendProducts = () => {
        products.forEach((product) => {
          this.grid.appendChild(product);
        });
        settle();
        // Stamped.io review badges for the new cards, when the app is installed.
        if (window.StampedFn && typeof window.StampedFn.loadBadges === 'function') {
          window.StampedFn.loadBadges();
        }
      };

      if (this.animations_enabled) {
        // GSAP staggers the new items in — a view transition on top
        // would snapshot them pre-animation and flicker. Append directly.
        appendProducts();
        gsap.set(products, {
          opacity: 0,
          y: 30
        });
        gsap.to(products, {
          duration: 0.5,
          y: 0,
          opacity: 1,
          stagger: 0.05,
          onComplete: function () {
            base.loaded();
          }
        });
      } else {
        withViewTransition(() => {
          appendProducts();
          base.loaded();
        });
      }
    }
    // The last page is on the grid: stop watching for the end of it.
    finish() {
      this.done = true;
      clearTimeout(this.retryTimer);
      this.observer?.disconnect();
      if (this.button) {
        this.button.style.display = 'none';
      }
    }
  }
  customElements.define('pagination-theme', Pagination);
}
